/**
 * Parses CSS background images.
 */

import _ from 'lodash';
import * as CssParser from './CssParser';
import * as GradientParser from './GradientParser';
import * as CssUtils from '@packages/systems/style-legacy/utils';
import * as FileUploadUtils from '@packages/utilities/file-upload';
import * as StringUtils from '@packages/utilities/string-utils';
import {splitCssValue} from '@packages/systems/core/utils/CssStringUtils';
import {reportBug} from '@packages/utilities/reporter';
import type {AssetState} from '@packages/systems/designer/assets';
import type {FileRecordType} from '@packages/systems/core/records/FileRecord';
import * as StyleVariables from '@packages/systems/core/utils/StyleVariables';

export const FILE_PREFIX = '@img_';

const IMAGE = 'image';
const POSITION = 'position';
const SIZE = 'size';
const REPEAT = 'repeat';
const ATTACHMENT = 'attachment';

const IMAGE_INFO = 'imageInfo';

const SUBVALUES = [
  {name: IMAGE, reset: 'none'},
  {name: POSITION, reset: 'left top', skipParse: true},
  {name: SIZE, reset: 'auto', skipParse: true},
  {name: REPEAT, reset: 'repeat'},
  {name: ATTACHMENT, reset: 'scroll'},
];

type PartJson = Record<any, any> | null | undefined;

// this should be the DesignerFlux instance, but circular dependencies
type DesignerFlux = any;
let flux: DesignerFlux = null;

/**
 * Specifies a Flux instance with an attached AssetStore, so that file IDs can be looked up.
 * @param {Flux} flux An instance of a flux instance that has an AssetStore.
 */
export function setFlux(instance: DesignerFlux) {
  flux = instance;
}

/**
 * Parses an individual part of a background-image property.
 * @param  {String} imageCss    The CSS value for one individual part.
 * @param  {String} mode        The upstream/local/browser mode we're currently parsing.
 * @param  {Object} partJson    The JSON object for this part from the parseParts() method.
 * @return {Object}             Parsed image details.
 */
export function parseImage(
  imageCss: string | null | undefined,
  mode: string,
  partJson: PartJson
) {
  imageCss = (imageCss || '')
    .replace(/^\/\*\s*(\S*)\s*\*\/$/, '$1')
    .toLowerCase();

  partJson = partJson || {};
  partJson[mode] = partJson[mode] || {};

  let imageGuid;

  // Gradient info is kept in a nested object
  // If both upstream and local has a gradient, the local data wins
  if (imageCss.indexOf('gradient(') > 0) {
    const variableMap =
      flux?.state?.CssVariablesStore?.variables ??
      StyleVariables.emptyVariableMap;

    partJson[mode].gradient = GradientParser.parse(imageCss, '', variableMap);
  } else if (_.startsWith(imageCss, 'url(')) {
    // If it's a standard URL, parse it out
    // RegExp will grab the inside of a url(), url(''), or url("")
    const url = imageCss.replace(
      /.*?url\(["']{0,1}([^'")]*)["']{0,1}\)?.*/,
      '$1'
    );
    const uploadData = FileUploadUtils.getUploadData(url);

    if (uploadData.url.indexOf('blob:') !== -1) return partJson;

    imageGuid = uploadData.guid;

    if (_processAsset(partJson, imageGuid, mode)) {
      // All is good
    } else {
      console.error(
        'Could not find image: %o, %o in asset store!',
        imageGuid,
        uploadData
      );
      partJson[mode][IMAGE_INFO] = uploadData;
    }
  } else if (_.startsWith(imageCss, FILE_PREFIX)) {
    // If it's a file placeholder, parse out the file id
    // If both the upstream and local file IDs exist, the local one will win since it comes later
    imageGuid = StringUtils.strRight(imageCss, FILE_PREFIX);
    _processAsset(partJson, imageGuid, mode);
  } else {
    // Otherwise report an error
    console.error('Unknown image format: %o', imageCss);
  }

  return partJson;
}

function _getAssetState() {
  if (flux) {
    return flux.getStoreState('AssetStore') as AssetState;
  } else {
    // Please see https://github.com/webflow/webflow/pull/39900
    // eslint-disable-next-line webflow/better-bug-reporting
    reportBug('InvalidStartup', 'Bg image parser has no flux reference.', {
      team: 'platform',
    });
    return null;
  }
}

function _withoutNulls(asset: FileRecordType) {
  return asset.toSeq().reduce<Record<string, any>>((memo, value, key) => {
    if (value != null) {
      memo[key] = value;
    }
    return memo;
  }, {});
}

// @ts-expect-error - TS7006 - Parameter 'partJson' implicitly has an 'any' type.
function _processAsset(partJson, origFileGuid: string, mode: string) {
  const assetStoreState = _getAssetState();

  if (!assetStoreState) {
    // If we don't have asset store reference, there is no point to continue.
    return false;
  }

  // unit tests and really old sites  will use getByid
  const imageInfo =
    assetStoreState.getById(origFileGuid) ||
    assetStoreState.getByGuid(origFileGuid);

  if (imageInfo) {
    const imageInfoJson = _withoutNulls(imageInfo);

    // Pass through the file info to BackgroundPart.js
    partJson[mode][IMAGE_INFO] = imageInfoJson;

    // Replace the file id in the final CSS to the actual URL
    const image = partJson[mode][IMAGE];
    const currentValue = image && image.value;
    if (currentValue) {
      image.value = currentValue.replace(
        FILE_PREFIX + origFileGuid,
        'url("' + imageInfo.cdnUrl + '")'
      );
    }

    return true;
  }

  return false;
}

// The StylePanel wants the raw cdnUrl.
// Add this to prevent breaking something else
export function parseImageReferenceCdnUrl(s: any) {
  if (_.startsWith(s, FILE_PREFIX)) {
    const assetStoreState = _getAssetState();

    if (!assetStoreState) {
      // If we don't have asset store reference, there is no point to continue.
      return s;
    }

    const imageGuid = StringUtils.strRight(s, FILE_PREFIX);
    let imageInfo =
      assetStoreState.getById(imageGuid) ||
      assetStoreState.getByGuid(imageGuid);

    // If we get here that means that the referenced image does not exist in the image store and has probably been deleted
    // There is no way to parse this reference anymore, so just default to the example image
    if (!imageInfo) {
      // Please see https://github.com/webflow/webflow/pull/39900
      // eslint-disable-next-line webflow/better-bug-reporting
      reportBug(
        'CorruptedImageFormat',
        'Encountered corrupted background image style during image reference resolution',
        {imageGuid, team: 'platform'}
      );

      imageInfo = assetStoreState.getById('example_img');

      if (!imageInfo) {
        // to make flow happy
        return s;
      }
    }

    return imageInfo.get('cdnUrl');
  } else {
    return s;
  }
}

/**
 * Parses an individual part of a background-position property.
 * @param  {String} positionCss The CSS value for one individual part.
 * @param  {String} mode        The upstream/local/browser mode we're currently parsing.
 * @param  {Object} partJson    The JSON object for this part from the parseParts() method.
 * @return {Object}             Parsed image position details.
 */
export function parsePosition(
  positionCss: string | null | undefined,
  mode: string,
  partJson: PartJson
) {
  positionCss = (positionCss || '').toLowerCase();
  partJson = partJson || {};
  partJson[mode] = partJson[mode] || {};

  const parts = splitCssValue(positionCss, ' ');
  if (parts.length > 0) {
    partJson[mode].left = CssUtils.toWebflowCssValueObject(parts[0]);

    if (parts.length > 1) {
      partJson[mode].top = CssUtils.toWebflowCssValueObject(parts[1]);
    }
  }

  return partJson;
}

/**
 * Parses an individual part of a background-repeat property.
 * @param  {String} cssImage The CSS value.
 * @return {Object}          Parsed repeat details.
 */
export function parseRepeat(cssValue?: string | null) {
  const repeatInfo: Record<string, any> = {};

  if (CssUtils.isRepeat(cssValue)) {
    repeatInfo.repeat = cssValue;
  }

  return repeatInfo;
}

/**
 * Parses an individual part of a background-size property.
 * @param  {String} sizeCss     The CSS value for one individual part.
 * @param  {String} mode        The upstream/local/browser mode we're currently parsing.
 * @param  {Object} partJson    The JSON object for this part from the parseParts() method.
 * @return {Object}             Parsed image size details.
 */
export function parseSize(
  sizeCss: string | null | undefined,
  mode: string,
  partJson: PartJson
) {
  sizeCss = (sizeCss || '').toLowerCase();
  partJson = partJson || {};
  partJson[mode] = partJson[mode] || {};

  // If the size is a predefined keyword, no need to parse
  if (sizeCss === 'cover' || sizeCss === 'contain' || sizeCss === 'inherit') {
    if (!partJson[mode][SIZE]) {
      partJson[mode][SIZE] = sizeCss;
    }

    return partJson;
  }

  const parts = splitCssValue(sizeCss, ' ');
  if (parts.length > 0) {
    partJson[mode].width = CssUtils.parseIfUnit(parts[0]);

    if (parts.length > 1) {
      partJson[mode].height = CssUtils.parseIfUnit(parts[1]);
    } else {
      partJson[mode].height = 'auto';
    }
  }

  return partJson;
}

/**
 * Parses a transition chunk of styles into a JSON list of parts.
 * @param  {Object} styleData The original values from StyleUtils.buildStyleMap.
 * @return {Object}           Parsed transition information.
 */
export function parseParts(styleData: PartJson) {
  // @ts-expect-error - TS2345 - Argument of type 'PartJson' is not assignable to parameter of type 'Object'.
  return CssParser.parseComplexParts(styleData, SUBVALUES, 'image', parseItem);
}

/**
 * Parses an individual subvalue for a specific part and populates the JSON object.
 * @param  {String} cssValue    The CSS value for this part/subvalue.
 * @param  {String} subvalue    The name of the subvalue (e.g. 'image', 'repeat', etc) being processed.
 * @param  {String} mode        The upstream/local/browser mode we're currently parsing.
 * @param  {Object} partJson    The JSON object for this part from the parseParts() method.
 */
function parseItem(
  cssValue: string,
  subvalue: string,
  mode: string,
  partJson: PartJson
) {
  partJson = partJson || {};

  if (StringUtils.isEmpty(cssValue)) {
    return {};
  }

  if (cssValue !== 'none') {
    if (subvalue === IMAGE) {
      parseImage(cssValue, mode, partJson);
    } else if (subvalue === POSITION) {
      parsePosition(cssValue, mode, partJson);
    } else if (subvalue === SIZE) {
      parseSize(cssValue, mode, partJson);
    } else if (subvalue === REPEAT) {
      // Repeat values are always strings, so nothing more to do here...
    } else if (subvalue === ATTACHMENT) {
      // Attachment values are always strings, so nothing more to do here...
    }
  }

  return partJson;
}
